import { useEffect, useRef, useState } from 'react';

import { api } from '@/api/client';
import { downloadWithProgress } from '@/lib/downloadProgress';

/**
 * Произношение слова, готовое к нажатию.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНЫЙ ХУК, ЕСЛИ ЕСТЬ `playWord`. Тот запрашивает ссылку В МОМЕНТ
 * НАЖАТИЯ, если её не успели подгрузить заранее. Пока идёт запрос, разрешение
 * на звук, выданное браузером под нажатие пальцем, истекает — и `play()`
 * отказывает МОЛЧА. Снаружи это выглядит как «кнопка не работает».
 *
 * ⚠️ ГОТОВНОСТЬ ОПРЕДЕЛЯЕТСЯ СКАЧАННЫМИ БАЙТАМИ, А НЕ СОБЫТИЯМИ ПРОИГРЫВАТЕЛЯ.
 * В camp мы уже обожглись: ждали `canplaythrough`, а он не приходил никогда —
 * элемент застревал на `stalled`, и кнопка оставалась серой навсегда. Это хуже
 * исходной беды. Обычный запрос либо удался, либо нет, и это работает везде.
 *
 * Порядок такой же, как в camp (`camp/src/audio.ts`):
 *
 *   1. Спрашиваем адрес звука у сервера при показе слова.
 *   2. Скачиваем сами байты обычным запросом.
 *   3. Кнопка включается, когда байты в руках, — нажатие уже не может упереться
 *      в незагруженную запись.
 */
export type WordAudio = {
  /** Запись скачана: кнопку можно нажимать. */
  ready: boolean;
  /** Идёт проигрывание. */
  playing: boolean;
  /** Получить или включить не удалось — кнопку показывать бесполезно. */
  failed: boolean;
  /**
   * Сколько записи скачано, 0…100 — или `null`, когда числа нет.
   *
   * ⚠️ ЗДЕСЬ ПРОЦЕНТ НАСТОЯЩИЙ, в отличие от общего плеера: запрос наш, и
   * `Content-Length` известен. Заказчик просил его ещё 05.09.2026 («пусть
   * загружается и виден прогресс»), но показывалась только крутилка.
   */
  percent: number | null;
  /** Включить. Ничего не делает, пока не готово. */
  play: () => void;
};

/** Адреса, уже полученные в этой сессии: слово → адрес звука. */
const urlCache = new Map<string, string>();

export function useWordAudio(
  word: string | undefined,
  /**
   * Готовый адрес записи, если он уже пришёл вместе со словом.
   *
   * ⚠️ ЭТО ГЛАВНЫЙ ПУТЬ, А НЕ ОПТИМИЗАЦИЯ. Ручка, которая ищет адрес по слову,
   * закрыта ключом `vocab`, а до старта обучения его у учеников нет — и звук
   * молчал у всех. Сам файл записи при этом ОТКРЫТ и отдаётся даже без входа,
   * поэтому имея адрес, мы обойдёмся без закрытой ручки вовсе.
   */
  knownUrl?: string | null,
): WordAudio {
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const [percent, setPercent] = useState<number | null>(null);
  const el = useRef<HTMLAudioElement | null>(null);
  const objectUrl = useRef<string | null>(null);

  useEffect(() => {
    setReady(false);
    setPlaying(false);
    setFailed(false);
    setPercent(null);
    if (!word) return;

    let alive = true;
    // ⚠️ Один элемент на весь срок жизни хука, а не `new Audio()` на нажатие:
    // одноразовый объект сборщик мусора убирал прямо во время проигрывания, и
    // слово обрывалось на середине — «reliable» звучало как «relia».
    const a = new Audio();
    a.preload = 'auto';
    el.current = a;
    // ⚠️ «Звучит» — ТОЛЬКО ПО СОБЫТИЮ. Раньше оно взводилось у вызова `play()`,
    // и кнопка говорила «звучит» раньше звука: отказ браузера или пустой файл
    // ученик видел как играющую кнопку в тишине.
    a.onplaying = () => alive && setPlaying(true);
    a.onended = () => alive && setPlaying(false);
    a.onpause = () => alive && setPlaying(false);

    const load = async () => {
      try {
        let url = knownUrl || urlCache.get(word);
        if (!url) {
          const { data } = await api.get<{ audio_url?: string }>('/vocab/student/pronounce', {
            params: { word },
          });
          url = (data as unknown as { audio_url?: string })?.audio_url;
          if (!url) throw new Error('сервер не вернул ссылку');
          urlCache.set(word, url);
        }
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) throw new Error(String(res.status));
        const blob = await downloadWithProgress(res, (p) => {
          if (alive) setPercent(p);
        });
        if (!alive) return;
        objectUrl.current = URL.createObjectURL(blob);
        a.src = objectUrl.current;
        setReady(true);
      } catch (e) {
        // ⚠️ Молчать нельзя: когда у поставщика синтеза кончились деньги,
        // кнопка нажималась, звука не было, и человек думал, что сломался
        // телефон. Узнали об этом только от заказчика.
        console.warn('[audio] произношение недоступно:', word, e);
        if (alive) setFailed(true);
      }
    };
    void load();

    return () => {
      alive = false;
      a.pause();
      if (objectUrl.current) {
        URL.revokeObjectURL(objectUrl.current);
        objectUrl.current = null;
      }
    };
  }, [word, knownUrl]);

  const play = () => {
    const a = el.current;
    if (!a || !ready) return;
    a.currentTime = 0;
    void a.play().catch((e) => {
      console.warn('[audio] браузер отказал в проигрывании:', e);
      setPlaying(false);
      setFailed(true);
    });
  };

  return { ready, playing, failed, percent, play };
}
